import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { Layout, Row, Col, Card } from 'antd';
import { PipelineFilter } from '../InvoicesTable/pipelineFilters/pipelineFilters';
import {
	changeFilterValueToArrayOfIds,
	changeFilterValueToArrayOfValues,
	getEndDateFieldName,
	getFiltersWithoutPaginationAndSorting,
	getStartDateFieldName,
	getStateSliceProp,
	getTupleNameFieldName,
	getTupleValueFieldName,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import SubnavBar from '../subnav_bar/SubnavBar';
import { AdvancedFilters } from '../advanced_filters/AdvancedFilters';
import queryString from 'querystring';
import {
	DEFAULT_SORTER,
	FILTER_CONFIG_NAMES,
	FILTER_FIELD_TYPE,
	FILTER_VALUE_TYPE,
} from '../../utils/DataConstants';
import SelectedFilters from '../selected_filters_pills/SelectedFilters';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';
import { withRouter } from 'react-router';
import {
	decodeUTCAdjustedDate,
	encodeUTCAdjustedDate,
	formatDateForParam,
	myTimeZone,
} from '../../utils/DataFormatterUtils';
import {
	filterOptionsBuilder,
	FILTER_OPTIONS,
	FILTER_OPTION_KEY_BUILDERS,
} from '../../utils/FilterUtils';
import moment from 'moment';
import OWAsyncTreeSelect from '../ow_async_tree_select/OWAsyncTreeSelect';
import OWDateRangePicker from '../ow_date_range_picker/OWDateRangePicker';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';
import OWAsyncTupleSelect from '../ow_async_select/OWAsyncTupleSelect';
import OWSelect from '../ow_radio_group/OWSelect';
import './PaginatedReduxTableWithHeader.less';

const { Content } = Layout;

const getFilterValue = (val, config, tz = null) => {
	switch (config.type) {
		case FILTER_FIELD_TYPE.RADIO_GROUP:
			return {
				[config.fieldName]: val === 'undefined' ? undefined : val,
			};
		case FILTER_FIELD_TYPE.DATE_RANGE:
			const timeZone = tz || myTimeZone();
			const startDate = val && val.length > 1 && val[0];
			const endDate = val && val.length > 1 && val[1];
			return {
				[getStartDateFieldName(config.fieldName)]: startDate
					? formatDateForParam(encodeUTCAdjustedDate(startDate, false, timeZone))
					: undefined,
				[getEndDateFieldName(config.fieldName)]: endDate
					? formatDateForParam(encodeUTCAdjustedDate(endDate, true, timeZone))
					: undefined,
			};
		case FILTER_FIELD_TYPE.TUPLE:
			return {
				[getTupleNameFieldName(config.fieldName)]:
					val && val.length > 0 && val[0] ? val[0] : undefined,
				[getTupleValueFieldName(config.fieldName)]:
					val && val.length > 1 && val[1] ? val[1] : undefined,
			};
		case FILTER_FIELD_TYPE.OW_MULTI_SELECT:
		default:
			return {
				[config.fieldName]: config.mode === 'multiple' ? val.join(',') || undefined : val,
			};
	}
};

const getFilterValueAccessor = (stateSlice, tcName, config) => {
	switch (config.type) {
		case FILTER_FIELD_TYPE.DATE_RANGE:
			const timeZone = myTimeZone();
			const startDate = decodeUTCAdjustedDate(
				nullSafeGet(`${tcName}.filters.${getStartDateFieldName(config.fieldName)}`, stateSlice),
				false,
				timeZone
			);
			const endDate = decodeUTCAdjustedDate(
				nullSafeGet(`${tcName}.filters.${getEndDateFieldName(config.fieldName)}`, stateSlice),
				true,
				timeZone
			);
			return [startDate && moment(startDate), endDate && moment(endDate), timeZone];
		case FILTER_FIELD_TYPE.TUPLE:
			const name = nullSafeGet(
				`${tcName}.filters.${getTupleNameFieldName(config.fieldName)}`,
				stateSlice
			);
			const value = nullSafeGet(
				`${tcName}.filters.${getTupleValueFieldName(config.fieldName)}`,
				stateSlice
			);
			return [name, value];
		case FILTER_FIELD_TYPE.RADIO_GROUP:
			const filters = nullSafeGet(`${tcName}.filters`, stateSlice);
			return typeof filters[config.fieldName] === 'undefined'
				? 'undefined'
				: `${filters[config.fieldName]}`;
		case FILTER_FIELD_TYPE.OW_MULTI_SELECT:
		default:
			const val = nullSafeGet(`${tcName}.filters.${config.fieldName}`, stateSlice);
			const accesor =
				config.mode === 'multiple'
					? config.valueType === FILTER_VALUE_TYPE.STRING
						? changeFilterValueToArrayOfValues(val)
						: changeFilterValueToArrayOfIds(val)
					: val;
			return accesor;
	}
};

const valueAccessor = (config) => (entity) => {
	switch (config.type) {
		case FILTER_FIELD_TYPE.DATE_RANGE:
			return entity;
		case FILTER_FIELD_TYPE.OW_MULTI_SELECT:
		case FILTER_FIELD_TYPE.RADIO_GROUP:
			return entity.value;
		case FILTER_FIELD_TYPE.TUPLE:
		default:
			return entity.id;
	}
};

const renderItem = (config) => (entity) => {
	switch (config.type) {
		case FILTER_FIELD_TYPE.DATE_RANGE:
			return entity;
		case FILTER_FIELD_TYPE.OW_MULTI_SELECT:
		case FILTER_FIELD_TYPE.RADIO_GROUP:
			return entity.label;
		case FILTER_FIELD_TYPE.TUPLE:
		default:
			return entity.name;
	}
};

const sortBy = { sort_by: 'name', order: 'ascend' };

const getComponent = (config) => {
	switch (config.type) {
		case FILTER_FIELD_TYPE.DATE_RANGE:
			return OWDateRangePicker;
		case FILTER_FIELD_TYPE.TUPLE:
			return OWAsyncTupleSelect;
		case FILTER_FIELD_TYPE.RADIO_GROUP:
			return OWRadioGroup;
		case FILTER_FIELD_TYPE.OW_MULTI_SELECT:
			return OWSelect;
		default:
			return OWAsyncTreeSelect;
	}
};

interface PaginatedReduxTableWithHeaderProps {
	targetCollectionName: String;
	updateFilters: (filter: any, tcName: String) => void;
	updateSorters?: (sort_by: String, order: String, tcName: String) => void;
	refreshCounts?: (filter: any) => void;
	clearAndUpdateFilters: (filter: any, tcName: String) => void;
	stateSlice: any;
	pipeLineFilters?: any[];
	sorters?: any[];
	location?: any;
	filterConfig: any[];
	/**
	 * Content for the header row's LEFT group, rendered before the search box and filter button —
	 * the mirror of `rightActions`. Meant for page-level controls that belong on the same line as
	 * the search, such as a sub-tab strip.
	 */
	leftActions?: any;
	rightActions: any;
	/**
	 * Moves the pipeline-filter chips BELOW the header row instead of above it. Only meaningful
	 * alongside `leftActions`: a sub-tab strip in the header has to sit above the chips it scopes,
	 * otherwise the page reads as though the chips switch the tabs rather than filter within one.
	 */
	pipelineFiltersBelowHeader?: boolean;
	emptyState: any;
	entityCollectionName: String;
	tableColumns: any[];
	fetchData: any;
	showHeader?: boolean;
	onTableRow?: any;
	rowSelection?: any;
	preAppliedFilters?: any;
	hasDefaultHeaderPage: boolean;
	initialPagination: any;
	initialSorters: any;
	hideFilters?: Boolean;
	hideSearch?: Boolean;
	additionalFilters?: any;
	components?: any;
	initialFilters?: any;
	keyAccessor?: any;
	useDistinctCollection?: Boolean;
	/**
	 * Render rows from the search projection rather than from a previously-read SQL record. Set it on
	 * a list whose columns only exist on the search doc — see getEntitiesByIdWithSearch.
	 */
	preferSearchRecords?: Boolean;
	ignoreLocationQueryParams?: Boolean;
	advancedFiltersInputParamName?: any;
	overridePipelineFilterKey?: string;
}

const PaginatedReduxTableWithHeader: FC<PaginatedReduxTableWithHeaderProps> = ({
	targetCollectionName,
	stateSlice,
	updateFilters,
	updateSorters,
	refreshCounts,
	clearAndUpdateFilters,
	pipeLineFilters,
	sorters,
	location,
	filterConfig = [],
	leftActions,
	rightActions,
	pipelineFiltersBelowHeader,
	emptyState,
	entityCollectionName,
	tableColumns,
	showHeader,
	onTableRow,
	fetchData,
	rowSelection,
	preAppliedFilters = {},
	hasDefaultHeaderPage,
	initialPagination = {},
	initialSorters = {},
	hideFilters,
	additionalFilters,
	initialFilters = {},
	components,
	hideSearch,
	keyAccessor,
	useDistinctCollection,
	preferSearchRecords,
	ignoreLocationQueryParams,
	advancedFiltersInputParamName,
	overridePipelineFilterKey,
}): React.ReactElement => {
	const queryParams = useMemo(() => {
		if (ignoreLocationQueryParams) {
			return {};
		}
		const searchString = nullSafeGetOrElse('search', location, '').startsWith('?')
			? location.search.slice(1)
			: location.search;
		return queryString.parse(searchString);
	}, [ignoreLocationQueryParams, location.search]);

	const derivedPagination = useMemo(() => {
		const { current: initialCurrent, pageSize: initialPageSize } = initialPagination;
		const defaultPagination = {
			current: initialCurrent || 1,
			pageSize: initialPageSize || 10,
		};
		const { current, pageSize } = queryParams;
		return {
			...defaultPagination,
			...(current && { current }),
			...(pageSize && { pageSize }),
		};
	}, [initialPagination, queryParams]);

	const initialFiltersWithQueryParams = useMemo(
		() => ({ ...getFiltersWithoutPaginationAndSorting(queryParams), ...initialFilters }),
		[queryParams]
	);

	const initialSorting = useMemo(() => {
		const { sort_by: querySortBy, order: queryOrder } = queryParams;
		const { sort_by: initialSortBy, order: initialOrder } = initialSorters;
		const sort_by = initialSortBy || querySortBy;
		const order = initialOrder || queryOrder;
		return {
			...DEFAULT_SORTER,
			...(sort_by && { sort_by }),
			...(order && { order }),
		};
	}, [initialSorters, queryParams]);

	useEffect(() => {
		refreshCounts && refreshCounts({ ...preAppliedFilters, ...initialFiltersWithQueryParams });
	}, []);

	const updatePipeLineFilters = useCallback(
		(filter) => updateFilters(filter, targetCollectionName),
		[targetCollectionName, updateFilters]
	);

	const pipeLineFilterValue = useMemo(
		() => nullSafeGet(`${targetCollectionName}.filters`, stateSlice),
		[stateSlice, targetCollectionName]
	);

	const applyFilters = useCallback(
		(filters) => {
			updateFilters(filters, targetCollectionName);
			const existingFilters = getStateSliceProp('filters', targetCollectionName, stateSlice);
			refreshCounts &&
				refreshCounts({
					...(existingFilters || {}),
					...(filters || {}),
				});
		},
		[refreshCounts, stateSlice, targetCollectionName, updateFilters]
	);

	const applySorting = useCallback(
		({ sort_by, order }) => updateSorters && updateSorters(sort_by, order, targetCollectionName),
		[targetCollectionName, updateSorters]
	);

	const activeSorters = useMemo(
		() => nullSafeGetOrElse(`${targetCollectionName}.sorting`, stateSlice, {}),
		[stateSlice, targetCollectionName]
	);

	const clearFilters = useCallback(
		(filters, tc) => {
			clearAndUpdateFilters(filters, tc);
			refreshCounts && refreshCounts(filters);
			applySorting(DEFAULT_SORTER);
		},
		[applySorting, clearAndUpdateFilters, refreshCounts]
	);

	const updatedFilterConfig = useMemo(
		() =>
			filterConfig.map((config) => ({
				...config,
				keyAccessor: config.keyAccessor || config.valueAccessor || valueAccessor(config),
				valueAccessor: config.valueAccessor || valueAccessor(config),
				renderItem: config.renderItem || renderItem(config),
				labelAccessor: config.labelAccessor || config.renderItem || renderItem(config),
				renderRecord: config.renderRecord || config.renderItem || renderItem(config),
				sortBy: config.sortBy || sortBy,
				placeholder: config.placeholder || config.placeholderText,
				component: config.component || getComponent(config),
				handleChange:
					config.handleChange ||
					(config.filterOptions && config.filterOptions.length > 0
						? (selectOption = FILTER_OPTIONS.IS) =>
								(ids) => {
									const normalizedIds = Array.isArray(ids) ? ids : ids != null ? [ids] : [];
									applyFilters(
										filterOptionsBuilder(
											selectOption,
											config.fieldName,
											normalizedIds,
											config.separator
										)
									);
								}
						: config.type === FILTER_FIELD_TYPE.DATE_RANGE
						? (val, tz) => applyFilters(getFilterValue(val, config, tz))
						: (val) => applyFilters(getFilterValue(val, config))),
				filtersValueAccessor: config.filtersValueAccessor
					? (ss, option) =>
							config.filtersValueAccessor(
								nullSafeGetOrElse(`${targetCollectionName}.filters`, ss, {}),
								option
							)
					: config.filterOptions && config.filterOptions.length > 0
					? (ss, selectedFilterOption = FILTER_OPTIONS.IS) => {
							const activeFieldName = FILTER_OPTION_KEY_BUILDERS[selectedFilterOption](
								config.fieldName
							);
							const val = nullSafeGet(`${targetCollectionName}.filters.${activeFieldName}`, ss);
							return config.mode === 'multiple'
								? config.valueType === FILTER_VALUE_TYPE.STRING
									? changeFilterValueToArrayOfValues(val)
									: changeFilterValueToArrayOfIds(val)
								: val;
					  }
					: (ss) => getFilterValueAccessor(ss, targetCollectionName, config),
				// Like filtersValueAccessor above, these expect the unwrapped active filters, but
				// AdvancedFilters passes the whole entity slice. Unwrap `[tc].filters` so location-based
				// area scoping and its note read the selected values correctly.
				additionalFiltersAccessor: config.additionalFiltersAccessor
					? (ss) =>
							config.additionalFiltersAccessor(
								nullSafeGetOrElse(`${targetCollectionName}.filters`, ss, {})
							)
					: undefined,
				noteAccessor: config.noteAccessor
					? (ss) =>
							config.noteAccessor(nullSafeGetOrElse(`${targetCollectionName}.filters`, ss, {}))
					: undefined,
			})),
		[applyFilters, filterConfig, targetCollectionName]
	);

	/**
	 * The chips, and the vertical space around them.
	 *
	 * Every gap here is ONE margin, never a pair left to collapse. The row used to carry `0.5em` top
	 * and bottom of its own, on top of the `0.5em` the header row above it and the table row below it
	 * already carry, so what the user actually saw depended on margin collapsing — and below the chips
	 * it does not simply collapse: an empty `SelectedFilters` wrapper sits between this row and the
	 * table, and the chips themselves carry a trailing `margin-bottom` (`.pipelineCounts__step`, which
	 * exists to space WRAPPED rows of chips and leaks below the last one). The space above and the
	 * space below therefore came out different, which is the bug.
	 *
	 * So: this row contributes no margin of its own where a neighbour already provides one, and the
	 * chips' trailing margin is neutralized locally — wrapped rows keep their spacing from the
	 * container's own `gap-y-1`, which is what should have been providing it in the first place. The
	 * top margin survives only in the chips-above-the-header arrangement, where this row is the first
	 * thing on the page and there is no neighbour above to space it off.
	 */
	const pipelineFilterRow =
		pipeLineFilters && pipeLineFilters.length > 0 ? (
			<Row
				className="paginated-redux-table-with-header__pipeline-row"
				style={{
					marginTop: pipelineFiltersBelowHeader ? 0 : '0.5em',
					marginBottom: 0,
					marginLeft: 0,
					marginRight: 0,
				}}
				gutter={16}
			>
				<PipelineFilter
					items={pipeLineFilters}
					subItems={{}}
					onChange={updatePipeLineFilters}
					value={pipeLineFilterValue}
					counts={stateSlice.counts}
					filterKey={overridePipelineFilterKey}
				/>
			</Row>
		) : null;

	return (
		<Content
			className="paginated-redux-table-with-header"
			style={{
				padding: '0 0.5em',
				...(hasDefaultHeaderPage && { marginTop: '-8px' }),
			}}
		>
			{pipelineFiltersBelowHeader ? null : pipelineFilterRow}
			<Row style={{ margin: '0.5em -8px' }} gutter={16}>
				<Col span={24}>
					<SubnavBar
						left={
							leftActions || !hideFilters ? (
								<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									{leftActions}
									{hideFilters ? null : (
										<AdvancedFilters
											updateFilters={applyFilters}
											applySorting={applySorting}
											sorters={activeSorters}
											sorterItems={sorters}
											defaultSorters={initialSorters}
											filterConfig={updatedFilterConfig}
											filtersTargetCollectionName={targetCollectionName}
											clearAndUpdateFilters={clearFilters}
											filtersStateSlice={stateSlice}
											preAppliedFilters={preAppliedFilters}
											initialPagination={{ current: 1, pageSize: 250 }}
											showSearch={!hideSearch}
											initialFilters={initialFiltersWithQueryParams}
											inputParamName={advancedFiltersInputParamName}
										/>
									)}
								</div>
							) : null
						}
						right={rightActions}
					/>
				</Col>
				{additionalFilters ? additionalFilters : null}
			</Row>
			{pipelineFiltersBelowHeader ? pipelineFilterRow : null}
			<div className="flex w-full flex-1 flex-row">
				<SelectedFilters
					updateFilters={applyFilters}
					filterValues={nullSafeGetOrElse(`${targetCollectionName}.filters`, stateSlice, {})}
					filterConfig={updatedFilterConfig}
				/>
			</div>
			<Row style={{ margin: '0.5em -8px' }} gutter={16}>
				<Col span={24}>
					<Card bodyStyle={{ padding: 8 }}>
						<PaginatedReduxTable
							mode="list"
							updateQueryParams={!ignoreLocationQueryParams}
							useDistinctCollection={useDistinctCollection}
							preferSearchRecords={preferSearchRecords}
							emptyState={emptyState}
							collectionName={entityCollectionName}
							targetCollectionName={targetCollectionName}
							columns={tableColumns}
							showHeader={!!showHeader}
							keyAccessor={keyAccessor ? keyAccessor : (el) => el.id}
							onRow={onTableRow}
							initialPagination={derivedPagination}
							initialSorting={initialSorting}
							initialFilters={{ ...initialFiltersWithQueryParams, ...preAppliedFilters }}
							fetchData={fetchData}
							rowSelection={rowSelection}
							components={components}
						/>
					</Card>
				</Col>
			</Row>
		</Content>
	);
};

export default withRouter<any, any>(PaginatedReduxTableWithHeader);
